<template>
    <div>
        <div class="columns" v-if="fightRecords.length === 0">
            <div class="column is-6 is-offset-3">
                <div class="notification is-info is-light has-text-centered">
                    <span class="icon">
                        <i class="fa fa-info-circle" />
                    </span>
                    <span>
                        {{ translate("record_profile.no_fights") }}
                    </span>
                </div>
            </div>
        </div>

        <template v-else>
            <div class="table-container is-hidden-touch">
                <table class="table is-fullwidth is-hoverable">
                    <thead>
                        <tr>
                            <th>{{ translate("record_profile.result") }}</th>
                            <th>{{ translate("record_profile.competitor") }}</th>
                            <th>{{ translate("record_profile.category") }}</th>
                            <th>{{ translate("record_profile.event") }}</th>
                            <th>{{ translate("record_profile.method") }}</th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr v-for="fight in fightRecords" :key="fight.id">
                            <td class="fight-result">
                                <template v-if="fight.opponent != null">
                                    <WinnerTag v-if="fight.winner" />

                                    <span class="tag is-danger" v-else>
                                        {{ translate("record_profile.result.loss") }}
                                    </span>
                                </template>
                                <span class="tag is-info" v-else-if="fight.bracketType === BracketTypeEnum.Kata">
                                    {{ translate("bracket_type.kata") }}
                                </span>
                            </td>

                            <td>
                                <template v-if="fight.opponent != null">
                                    <a @click="emit('selectOpponent', fight.eventId, fight.opponent.id)">
                                        <div class="clarity-mask">
                                            {{ fight.opponent.firstName }}
                                            {{ fight.opponent.lastName }}
                                        </div>
                                        <div style="font-size: 0.7em">
                                            {{ fight.opponent.academyName }} /
                                            {{ fight.opponent.branch }}
                                        </div>
                                    </a>
                                </template>

                                <span v-else>—</span>
                            </td>
                            <td>
                                <a :href="fight.bracketUrl" target="_blank">
                                    {{ fight.categoryName }}
                                </a>
                            </td>
                            <td>
                                <div>
                                    <a :href="fight.eventUrl" target="_blank">
                                        {{ fight.eventFullName }}
                                    </a>
                                </div>
                                <div style="font-size: 0.7em">
                                    {{ autoConvertToTimezone(fight.finished) }}
                                </div>
                            </td>
                            <td>
                                <Result :result="fight.result" />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="is-hidden-desktop fight-cards">
                <div v-for="fight in fightRecords" :key="fight.id" class="box has-no-glow-effect fight-card">
                    <div class="is-flex is-justify-content-space-between is-align-items-flex-start mb-2">
                        <div class="fight-result">
                            <template v-if="fight.opponent != null">
                                <WinnerTag v-if="fight.winner" />
                                <span class="tag is-danger" v-else>
                                    {{ translate("record_profile.result.loss") }}
                                </span>
                            </template>
                            <span class="tag is-info" v-else-if="fight.bracketType === BracketTypeEnum.Kata">
                                {{ translate("bracket_type.kata") }}
                            </span>
                        </div>
                        <div class="has-text-right">
                            <Result :result="fight.result" />
                        </div>
                    </div>

                    <div class="mb-2">
                        <template v-if="fight.opponent != null">
                            <a @click="emit('selectOpponent', fight.eventId, fight.opponent.id)">
                                <div class="has-text-link clarity-mask">{{ fight.opponent.firstName }} {{ fight.opponent.lastName }}</div>
                                <div class="is-size-7 has-text-grey-light mt-1">
                                    {{ fight.opponent.academyName }} / {{ fight.opponent.branch }}
                                </div>
                            </a>
                        </template>
                        <span class="has-text-grey-light" v-else>—</span>
                    </div>

                    <p class="is-size-7 mt-1">
                        <span class="has-text-grey-light"> {{ translate("record_profile.category") }}: </span>
                        <a :href="fight.bracketUrl" target="_blank" class="has-text-white">{{ fight.categoryName }}</a>
                    </p>
                    <p class="is-size-7 mt-1">
                        <span class="has-text-grey-light"> {{ translate("record_profile.event") }}: </span>
                        <a :href="fight.eventUrl" target="_blank" class="has-text-white">{{ fight.eventFullName }}</a>
                    </p>
                    <p class="is-size-7 mt-1 has-text-grey-light">
                        {{ autoConvertToTimezone(fight.finished) }}
                    </p>
                </div>
            </div>
        </template>

        <div class="columns" v-if="canLoadMore">
            <div class="column is-12 has-text-centered">
                <LoadingButton class="button is-link" @click="emit('loadMore')" :is-loading="loadingMore">
                    {{ translate("record_profile.load_more_fights") }}
                </LoadingButton>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import Result from "./Result.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import WinnerTag from "../Common/Fights/WinnerTag.vue";
    import { translate } from "../Common/translations";
    import { autoConvertToTimezone } from "../Common/dateTimeUtils";
    import { FullFightRecord } from "../Api/recordProfile";
    import { BracketTypeEnum } from "../Common/types";

    defineProps<{
        fightRecords: FullFightRecord[];
        canLoadMore: boolean;
        loadingMore: boolean;
    }>();

    const emit = defineEmits<{
        selectOpponent: [eventId: number, opponentId: string];
        loadMore: [];
    }>();
</script>

<style scoped>
    .table td,
    .table th {
        padding: 0.25em 0.35em;
    }

    .table {
        font-size: 0.9em;
    }

    .fight-result {
        text-transform: uppercase;
    }

    .fight-cards {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .fight-card {
        padding: 0.75rem 1rem;
    }
</style>
